import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { Device } from '@twilio/voice-sdk';
import VoiceCallService from '../services/VoiceCallService';

// Create context
const ActiveCallContext = createContext(null);

/**
 * ActiveCallProvider - Manages global active call state
 * Allows any component to initiate calls and track active call status
 * Now with Twilio Device SDK for browser-to-phone WebRTC calling
 */
export const ActiveCallProvider = ({ children }) => {
  // Active call state
  const [activeCall, setActiveCall] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  
  // Twilio Device state - these are prepared for future WebRTC calling feature
  // eslint-disable-next-line no-unused-vars
  const [device, setDevice] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [deviceReady, setDeviceReady] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deviceError, setDeviceError] = useState(null);
  
  // Refs
  const pollingIntervalRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const currentCallRef = useRef(null); // Twilio Call object
  const deviceRef = useRef(null);
  const identityRef = useRef(undefined); // shared inbound identity once known
  const [incomingCall, setIncomingCall] = useState(null);

  // Initialize Twilio Device
  const initializeDevice = useCallback(async () => {
    try {
      console.log('🔧 [Twilio Device] Initializing...');
      
      // Get access token from backend
      let tokenResponse;
      try {
        tokenResponse = await VoiceCallService.getAccessToken(identityRef.current);
      } catch (tokenError) {
        console.error('❌ [Twilio Device] Failed to get access token:', tokenError);
        const errorMsg = tokenError?.message || 'Failed to get access token';
        setDeviceError(errorMsg);
        return null;
      }
      
      if (!tokenResponse?.success || !tokenResponse?.token) {
        const errorMsg = tokenResponse?.error || 'Failed to get access token';
        console.error('❌ [Twilio Device] Invalid token response:', errorMsg);
        setDeviceError(errorMsg);
        return null;
      }
      
      console.log('🔑 [Twilio Device] Token received, identity:', tokenResponse.identity);
      
      // Create new Device
      const newDevice = new Device(tokenResponse.token, {
        logLevel: 0,
        codecPreferences: ['opus', 'pcmu'],
        edge: 'ashburn'
      });
      
      // Set up Device event handlers
      newDevice.on('registered', () => {
        console.log('✅ [Twilio Device] Registered successfully');
        setDeviceReady(true);
        setDeviceError(null);
      });
      
      newDevice.on('error', (error) => {
        console.error('❌ [Twilio Device] Error:', error);
        setDeviceError(error?.message || 'Unknown device error');
      });
      
      newDevice.on('incoming', (call) => {
        const params = call.customParameters;
        const from = params?.get('callerNumber') || call.parameters?.From || '';
        const callerName = params?.get('callerName') || '';
        console.log('📞 [Twilio Device] Incoming call');
        setIncomingCall({ call, from, callerName });
        // Someone else answered, the caller hung up, or it timed out.
        const clear = () => setIncomingCall((cur) => (cur?.call === call ? null : cur));
        call.on('cancel', clear);
        call.on('disconnect', clear);
        call.on('reject', clear);
      });

      // Tokens last an hour; keep the device registered for inbound calls.
      newDevice.on('tokenWillExpire', async () => {
        try {
          const res = await VoiceCallService.getAccessToken(identityRef.current);
          if (res?.token) newDevice.updateToken(res.token);
        } catch (err) {
          console.error('❌ [Twilio Device] Token refresh failed:', err);
        }
      });
      
      // Register the device
      await newDevice.register();
      
      deviceRef.current = newDevice;
      setDevice(newDevice);
      console.log('✅ [Twilio Device] Initialized and registered');
      
      return newDevice;
    } catch (error) {
      console.error('❌ [Twilio Device] Initialization failed:', error);
      setDeviceError(error?.message || 'Device initialization failed');
      return null;
    }
  }, []);

  // Twilio Device is initialized lazily on the first outbound call (see
  // makeDeviceCall / initiateCall) rather than on mount. Initializing eagerly
  // instantiated the Twilio SDK on every page, which preloads all of its
  // DTMF/ringtone .mp3 sounds — unnecessary network work outside of a call.
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, []);

  // When inbound calls ring staff browsers, register this browser up front so it can ring.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const res = await VoiceCallService.getInboundConfig().catch(() => null);
      if (cancelled || !res?.success) return;
      identityRef.current = res.data.identity;
      if (res.data.mode === 'browser' && !deviceRef.current) {
        await initializeDevice();
      }
    };
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [initializeDevice]);

  const acceptIncoming = useCallback(() => {
    const incoming = incomingCall;
    if (!incoming) return;
    const { call, from, callerName } = incoming;
    call.accept();
    currentCallRef.current = call;
    setIncomingCall(null);
    setIsCallActive(true);
    setActiveCall({
      status: 'in-progress',
      direction: 'inbound',
      fromNumber: from,
      toNumber: 'You',
      contact: callerName ? { name: callerName } : null,
      callType: 'personal',
      callSid: null,
      startTime: Date.now(),
      duration: 0
    });
    const done = () => {
      currentCallRef.current = null;
      setIsCallActive(false);
      setActiveCall(null);
    };
    call.on('disconnect', done);
    call.on('cancel', done);
  }, [incomingCall]);

  // Stops ringing here only; other staff browsers keep ringing.
  const dismissIncoming = useCallback(() => {
    incomingCall?.call?.ignore();
    setIncomingCall(null);
  }, [incomingCall]);

  // Make a call using Twilio Device
  const makeDeviceCall = useCallback(async (phoneNumber, callId) => {
    try {
      // Ensure device is ready
      let activeDevice = deviceRef.current || device;
      if (!activeDevice) {
        console.log('🔧 [Twilio Device] Device not ready, initializing...');
        activeDevice = await initializeDevice();
        if (!activeDevice) {
          throw new Error('Failed to initialize Twilio Device');
        }
      }
      
      console.log('📞 [Twilio Device] Making call');
      
      // Connect the call - Twilio will call our browser-call-twiml webhook
      const call = await activeDevice.connect({
        params: {
          To: phoneNumber,
          callId: callId || ''
        }
      });
      
      currentCallRef.current = call;
      
      // Set up call event handlers
      call.on('accept', () => {
        console.log('✅ [Twilio Call] Call accepted/connected');
        setActiveCall(prev => ({
          ...prev,
          status: 'in-progress',
          startTime: Date.now()
        }));
      });
      
      call.on('ringing', () => {
        console.log('🔔 [Twilio Call] Ringing...');
        setActiveCall(prev => ({
          ...prev,
          status: 'ringing'
        }));
      });
      
      call.on('disconnect', () => {
        console.log('📴 [Twilio Call] Call disconnected');
        currentCallRef.current = null;
        setIsCallActive(false);
        setActiveCall(null);
      });
      
      call.on('cancel', () => {
        console.log('❌ [Twilio Call] Call cancelled');
        currentCallRef.current = null;
        setIsCallActive(false);
        setActiveCall(null);
      });
      
      call.on('error', (error) => {
        console.error('❌ [Twilio Call] Error:', error);
        setActiveCall(prev => ({
          ...prev,
          status: 'failed',
          error: error?.message || 'Call error'
        }));
        setTimeout(() => {
          currentCallRef.current = null;
          setIsCallActive(false);
          setActiveCall(null);
        }, 3000);
      });
      
      return call;
    } catch (error) {
      console.error('❌ [Twilio Device] Call failed:', error);
      throw new Error(error?.message || 'Failed to make call');
    }
  }, [device, initializeDevice]);

  // Start polling for call status (fallback for non-Device calls)
  // eslint-disable-next-line no-unused-vars
  const startStatusPolling = useCallback((callSid) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await VoiceCallService.getCallStatus(callSid);
        
        if (statusResponse.success) {
          const status = statusResponse.data?.status || statusResponse.status;
          
          console.log('📞 [Call Status Poll]', status);
          
          // Update call status based on response
          setActiveCall(prev => {
            if (!prev) return null;
            
            const updates = { ...prev, status };
            
            // Start timer when call is in-progress (connected)
            if (status === 'in-progress' && !prev.startTime) {
              updates.startTime = Date.now();
            }
            
            return updates;
          });
          
          // End polling and clear call if call ended
          if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(status)) {
            console.log('📞 [Call Ended]', status);
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            
            // Keep showing for a moment then clear
            setTimeout(() => {
              setIsCallActive(false);
              setActiveCall(null);
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error polling call status:', error);
      }
    }, 2000);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Initiate a new call using Twilio Device (browser-to-phone via WebRTC)
  const initiateCall = useCallback(async (callDetails) => {
    const {
      fullNumber,
      contact,
      agent,
      callType,
      fromNumber,
      // eslint-disable-next-line no-unused-vars
      patientId,
      // eslint-disable-next-line no-unused-vars
      context
    } = callDetails;

    try {
      // Set call as active immediately for UI feedback
      setIsCallActive(true);
      setActiveCall({
        status: 'initiating',
        toNumber: fullNumber,
        fromNumber: fromNumber || 'Unknown',
        contact: contact,
        agent: agent,
        callType: callType,
        callSid: null,
        startTime: null,
        duration: 0
      });

      // Use Twilio Device for browser-to-phone calling
      console.log('📞 [ActiveCall] Initiating call via Twilio Device to:', fullNumber);
      
      // Make call via Twilio Device (uses WebRTC)
      await makeDeviceCall(fullNumber);
      
      return { success: true };
    } catch (error) {
      console.error('Error initiating call:', error);
      const errorMsg = error?.message || String(error) || 'Call failed';
      setActiveCall(prev => ({
        ...prev,
        status: 'failed',
        error: errorMsg
      }));
      
      // Clear after short delay to show error
      setTimeout(() => {
        setIsCallActive(false);
        setActiveCall(null);
      }, 3000);
      
      return { success: false, error: errorMsg };
    }
  }, [makeDeviceCall]);

  // Update call status
  const updateCallStatus = useCallback((status, additionalData = {}) => {
    setActiveCall(prev => ({
      ...prev,
      status,
      ...additionalData
    }));
    
    if (status === 'connected' && !activeCall?.startTime) {
      setActiveCall(prev => ({
        ...prev,
        startTime: Date.now()
      }));
    }
  }, [activeCall]);

  // End the active call
  const endCall = useCallback(async () => {
    console.log('📴 [ActiveCall] Ending call...');
    
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Disconnect Twilio call if active
    if (currentCallRef.current) {
      try {
        currentCallRef.current.disconnect();
        console.log('✅ [ActiveCall] Twilio call disconnected');
      } catch (error) {
        console.error('Error disconnecting Twilio call:', error);
      }
      currentCallRef.current = null;
    }
    
    // Also try API endpoint for cleanup
    if (activeCall?.callSid) {
      try {
        await VoiceCallService.endCall(activeCall.callSid);
      } catch (error) {
        console.error('Error ending call via API:', error);
      }
    }
    
    setIsCallActive(false);
    setActiveCall(null);
  }, [activeCall]);

  // Toggle mute
  const toggleMute = useCallback((muted) => {
    // Mute/unmute the Twilio call if active
    if (currentCallRef.current) {
      currentCallRef.current.mute(muted);
      console.log(`🔇 [ActiveCall] Call ${muted ? 'muted' : 'unmuted'}`);
    }
    
    setActiveCall(prev => ({
      ...prev,
      isMuted: muted
    }));
  }, []);

  // Context value
  const value = {
    activeCall,
    isCallActive,
    initiateCall,
    updateCallStatus,
    endCall,
    toggleMute,
    deviceReady,
    deviceError,
    incomingCall,
    acceptIncoming,
    dismissIncoming
  };

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
    </ActiveCallContext.Provider>
  );
};

/**
 * Hook to access active call context
 */
export const useActiveCall = () => {
  const context = useContext(ActiveCallContext);
  if (!context) {
    throw new Error('useActiveCall must be used within an ActiveCallProvider');
  }
  return context;
};

export default ActiveCallContext;